import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ThemeProvider } from './contexts/ThemeContext';
import FrameMeter from './components/FrameMeter'; // [FRAME-METER 2026-08-14]
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { GlobalStyles } from './styles/GlobalStyles';
// Installs the app-wide sticky-hover gate (keeps the table row-hover tint from
// getting "stuck" on a row after scrolling / a silent reload / tab-switch).
import './utils/hoverGate';
import { DialogHost } from './components/dialogService';
import { ToastHost } from './components/toastService';
import GlobalTooltip from './components/GlobalTooltip';
import UpdateBanner from './components/UpdateBanner';
import { SECTIONS, SECTION_KEYS, USER_MANAGEMENT, APPLICATION_LOGS, MY_INVENTORY, PM_SINGAPORE, SALES_ROW, DUBAI_CONTEST } from './config/sections';
import AppLayout from './layout/AppLayout';
import LoginPage from './pages/LoginPage';
import StatsPage from './pages/StatsPage';
import PaymentsPage from './pages/PaymentsPage';
import DelayedPaymentsPage from './pages/DelayedPaymentsPage';
import PlaceholderPage from './pages/PlaceholderPage';
import InventoryPage from './pages/InventoryPage';
import NewSalesPage from './pages/NewSalesPage';
import MySalesPage from './pages/MySalesPage';
import SalesPipelinePage from './pages/SalesPipelinePage'; // [SALES-PIPELINE 2026-10-07]
import LeadAssignmentPage from './pages/LeadAssignmentPage'; // [LEAD-ASSIGNMENT 2026-10-13]
import TeamSameerReportPage from './pages/TeamSameerReportPage'; // [TEAM-SAMEER 2026-10-15]
import LeadGeneratorPerformancePage from './pages/LeadGeneratorPerformancePage'; // [LGP 2026-10-15]
import DtDailyReportPage from './pages/DtDailyReportPage'; // [DT-DAILY 2026-10-16]
import SalesScoreboardPage from './pages/SalesScoreboardPage';
import ProcessAnalysisPage from './pages/ProcessAnalysisPage';
import PmRecommendationsPage from './pages/PmRecommendationsPage'; // [PM-RECS 2026-08-25]
import MyPerformancePage from './pages/MyPerformancePage'; // [MY-PERF 2026-09-02]
import PerformancePage from './pages/PerformancePage';
import DialingReportPage from './pages/DialingReportPage';
import RefundsDisputesPage from './pages/RefundsDisputesPage'; // [RD-PILL 2026-09-10] serves /refunds + /disputes
import EOMPage from './pages/EOMPage';
import MonthOnMonthPage from './pages/MonthOnMonthPage';
import RetentionAnalysisPage from './pages/RetentionAnalysisPage';
import TeamPerformancePage from './pages/TeamPerformancePage';
import UserManagementPage from './pages/UserManagementPage';
import PMSingaporePage from './pages/PMSingaporePage';
import SalesRowPage from './pages/SalesRowPage'; // [SALES-ROW 2026-09-30]
import DubaiContestPage from './pages/DubaiContestPage'; // [DUBAI 2026-10-19]
import ApplicationLogsPage from './pages/ApplicationLogsPage';
import MyInventoryPage from './pages/MyInventoryPage';
import InventoryViewPage from './pages/InventoryViewPage';
import CampaignHistoryPage from './pages/CampaignHistoryPage'; // [CAMPAIGN-HISTORY 2026-09-04]

const spin = keyframes`to { transform: rotate(360deg); }`;

const FullGreen = styled.div`
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 24px;
  background: linear-gradient(135deg, #0c5b47 0%, #146b54 55%, #0c5b47 100%);
`;

const Spinner = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.25);
  border-top-color: #ffffff;
  animation: ${spin} 0.8s linear infinite;
`;

/* [DB-OUTAGE 2026-11-03, user] Under the spinner while the first sign-in check keeps asking a server that is not
   answering (contexts/AuthContext), instead of the login page. Same green screen on laptop and phone. */
const LoadingStack = styled.div`
  display: grid;
  justify-items: center;
  gap: 16px;
`;

const ReconnectNote = styled.p`
  margin: 0;
  max-width: 300px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  color: #ffffff;
`;

const NoAccessCard = styled.div`
  width: 100%;
  max-width: 440px;
  padding: 40px 36px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 30px 70px rgba(0, 0, 0, 0.3);
  text-align: center;

  .icon {
    width: 60px;
    height: 60px;
    margin: 0 auto 18px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    font-size: 24px;
    color: #146b54;
    background: rgba(20, 107, 84, 0.1);
  }
  h1 { margin: 0 0 8px; font-size: 21px; font-weight: 800; color: #161917; }
  p { margin: 0 0 6px; font-size: 14px; line-height: 1.5; color: #6f7472; }
  .email { font-weight: 600; color: #146b54; }
  button {
    margin-top: 22px;
    height: 44px;
    padding: 0 22px;
    border-radius: 11px;
    border: 1px solid #dadce0;
    background: #fff;
    color: #3c4043;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  button:hover { background: #f8faff; }

  /* [PHONE-LAYOUT 2026-10-22] Phones: less side padding so the message gets the card's width. */
  @media (max-width: 768px) {
    padding: 32px 22px;
  }
`;

/** Page component for each grantable section, keyed by section key. */
// NOTE on the EOM naming (historical, intentionally documented here):
//   key `dashboard` → nav "EOM"            → EOMPage          (DB-backed end-of-month dashboard)
//   key `eom`       → nav "Month on Month" → MonthOnMonthPage (the master Sheet's EOM tab, via /eom-table)
// The keys are stored in each user's allowed_sections in the DB, so they can't
// be renamed without a grant migration — hence the label/component names carry
// the clarity instead. MonthOnMonthPage is the only Sheets-backed page.
const PAGE_BY_KEY: Record<string, React.ReactNode> = {
  'lead-assignment': <LeadAssignmentPage />, // [LEAD-ASSIGNMENT 2026-10-13] the lead mailboxes' emails, marked Leads / Not Leads
  'team-sameer-report': <TeamSameerReportPage />, // [TEAM-SAMEER 2026-10-15] Team Sameer's lead generators between two dates
  'lead-generator-performance': <LeadGeneratorPerformancePage />, // [LGP 2026-10-15] the Apps Script dashboard on our database
  'dt-daily-report': <DtDailyReportPage />, // [DT-DAILY 2026-10-16] the Apps Script "SEO Daily Leads Assignment Report" on our server
  dashboard: <EOMPage />,
  stats: <StatsPage />,
  payments: <PaymentsPage />,
  'delayed-payments': <DelayedPaymentsPage />,
  eom: <MonthOnMonthPage />,
  'retention-analysis': <RetentionAnalysisPage />,
  // [RD-PILL 2026-09-10] ONE page for both routes — it slides between Refunds and Disputes. Each
  // route still exists only for a user granted that key, exactly as before.
  refunds: <RefundsDisputesPage />,
  disputes: <RefundsDisputesPage />,
  // [INV-HISTORY-BTN 2026-09-10, user] historyButton = the website cell's third button (Campaign History).
  inventory: <InventoryPage adminEditOnly showTypeFilter={false} historyButton />,
  'new-sales': <NewSalesPage />,
  'sales-pipeline': <SalesPipelinePage />, // [SALES-PIPELINE 2026-10-07]
  'my-sales': <MySalesPage />,
  'sales-scoreboard': <SalesScoreboardPage />,
  'process-analysis': <ProcessAnalysisPage />,
  'pm-recommendations': <PmRecommendationsPage />, // [PM-RECS 2026-08-25]
  'my-performance': <MyPerformancePage />, // [MY-PERF 2026-09-02]
  performance: <PerformancePage />,
  'dialing-report': <DialingReportPage />,
  'application-logs': <ApplicationLogsPage />,
  'my-inventory': <MyInventoryPage />,
  'inventory-view': <InventoryViewPage />,
  'team-performance': <TeamPerformancePage teamKey="ilma" title="Ilma Performance" />,
  'shreyansh-performance': <TeamPerformancePage teamKey="shreyansh" title="Shreyansh Performance" />,
  'ic-performance': <TeamPerformancePage teamKey="ic" title="IC Performance" />,
};

const AppRoutes: React.FC<{
  isAdmin: boolean;
  visibleKeys: Set<string>;
  landingPath: string;
  canPmSingapore: boolean;
  canSalesRow: boolean; // [SALES-ROW 2026-09-30]
  canDubai: boolean; // [DUBAI 2026-10-19]
}> = ({ isAdmin, visibleKeys, landingPath, canPmSingapore, canSalesRow, canDubai }) => {
  // A hard refresh keeps you on whatever section you were viewing: the Router
  // simply initialises on the reloaded URL. Only the root path "/" and any
  // route the user can't access fall back to the landing section (see the
  // <Navigate> routes below). We intentionally do NOT rewrite the address bar
  // on mount — doing so used to force every refresh back to Payments.
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<AppLayout />}>
          {SECTIONS.filter((s) => visibleKeys.has(s.key)).map((s) => (
            <Route key={s.key} path={s.path} element={PAGE_BY_KEY[s.key]} />
          ))}
          {isAdmin && <Route path={USER_MANAGEMENT.path} element={<UserManagementPage />} />}
          {/* [PM-LOGIN 2026-08-11] Gated on the same condition as the nav pill, so the route does
              not exist for anyone else — typing the URL falls through to the catch-all Navigate
              below rather than rendering the page. The server enforces it independently. */}
          {canPmSingapore && <Route path={PM_SINGAPORE.path} element={<PMSingaporePage />} />}
          {/* [SALES-ROW 2026-09-30] Same rule for the Team ROW Sales board. */}
          {canSalesRow && <Route path={SALES_ROW.path} element={<SalesRowPage />} />}
          {/* [DUBAI 2026-10-19] Same rule for the Dubai contest board: only its own sign-in has the route. */}
          {canDubai && <Route path={DUBAI_CONTEST.path} element={<DubaiContestPage />} />}
          {/* [CAMPAIGN-HISTORY 2026-09-04] A DRILL-DOWN, not a tab: opened by clicking a website in
              My Inventory, so it deliberately stays OUT of SECTIONS — adding it there would give it
              a nav pill of its own. It therefore has to be registered by hand, because the catch-all
              <Route path="*"> below silently redirects anything unregistered to the landing page.
              Shown to anyone who can reach an inventory row; the server checks the caller can
              actually see the campaign, and sectionGuard gates the API on the same three sections. */}
          {(isAdmin || visibleKeys.has('my-inventory') || visibleKeys.has('inventory') || visibleKeys.has('inventory-view')) && (
            <Route path="/campaign-history" element={<CampaignHistoryPage />} />
          )}
          {isAdmin && <Route path="/settings" element={<PlaceholderPage />} />}
          <Route path="/" element={<Navigate to={landingPath} replace />} />
          {/* Anything not permitted falls back to the user's landing section. */}
          <Route path="*" element={<Navigate to={landingPath} replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

const NoAccess: React.FC<{ email: string; onLogout: () => void }> = ({ email, onLogout }) => (
  <FullGreen>
    <NoAccessCard>
      <div className="icon">
        <i className="fas fa-lock" />
      </div>
      <h1>No access yet</h1>
      <p>
        You're signed in as <span className="email">{email}</span>.
      </p>
      <p>Your account hasn't been assigned any sections yet. Please ask an administrator to grant you access.</p>
      <button onClick={onLogout}>Sign out</button>
    </NoAccessCard>
  </FullGreen>
);

/** Decides what to render based on session + permissions. */
const Gate: React.FC = () => {
  const { user, loading, reconnecting, logout } = useAuth();

  if (loading) {
    return (
      <FullGreen>
        <LoadingStack>
          <Spinner />
          {reconnecting && (
            <ReconnectNote role="status">The server is not answering right now. Trying again by itself…</ReconnectNote>
          )}
        </LoadingStack>
      </FullGreen>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const isAdmin = !!user.is_admin;
  const visibleKeys = new Set(
    isAdmin ? SECTION_KEYS : (user.allowed_sections || []).filter((k) => SECTION_KEYS.includes(k))
  );
  // Team owners (leads) automatically get the Performance section (scoped to their
  // team inside the page), even if it wasn't granted explicitly. Regular team
  // members are NOT auto-granted — they see the section only if it was granted,
  // and the page then scopes them to their own team's tab.
  if (!isAdmin && user.performance_owner) visibleKeys.add('performance');

  /* [PM-LOGIN 2026-08-11] PM Singapore is not in SECTIONS (it is not grantable), so it can never
     appear in visibleKeys and has to be resolved separately.
     [PM-SG 2026-08-11] ONE way in and no second: the explicit `pm-singapore` grant, which only
     the shared credentials account carries. Admins deliberately do NOT get it — the user's call,
     so that Google sign-in behaves exactly as it did before this feature existed. The API is
     gated on the same section key (middleware/sectionGuard). */
  const canPmSingapore = (user.allowed_sections || []).includes(PM_SINGAPORE.key);
  // [SALES-ROW 2026-09-30, user] The Team ROW Sales board.
  // [ROW-BUTTON 2026-09-30, user] Decided on the server — admins, Team ROW - Sales and Vishal Suryavanshi since
  // [ROW-ACCESS-TEAM 2026-10-01] (services/salesRowAccess.ts) — the same
  // flag that shows the header's "ROW Dashboard" button.
  const canSalesRow = !!user.can_sales_row;
  // [DUBAI 2026-10-19, user] The Dubai contest board: the explicit grant only its own sign-in carries (like PM Singapore).
  const canDubai = (user.allowed_sections || []).includes(DUBAI_CONTEST.key);

  /* Without the canPmSingapore term the password account would land on "No access yet": its only
     section is not one of SECTIONS, so visibleKeys is empty for it. Same for canSalesRow and canDubai. */
  if (!isAdmin && visibleKeys.size === 0 && !canPmSingapore && !canSalesRow && !canDubai) {
    return <NoAccess email={user.email} onLogout={logout} />;
  }

  // Prefer Payments as the landing section when available (matches prior default),
  // otherwise the first section the user can see.
  const ordered = SECTIONS.filter((s) => visibleKeys.has(s.key));
  /* `ordered` is EMPTY for the password account, and the old `ordered[0].path` would have thrown
     on undefined before anything rendered. PM Singapore is the fallback landing page, which is
     also exactly where that account should open. [SALES-ROW 2026-09-30] The Sales ROW account's
     `ordered` is empty too, and it opens on its own board instead. */
  const landingPath =
    (ordered.find((s) => s.path === '/payments') || ordered[0])?.path
    ?? (canDubai ? DUBAI_CONTEST.path : canSalesRow ? SALES_ROW.path : PM_SINGAPORE.path); // [DUBAI 2026-10-19] its sign-in opens on its board

  return (
    <AppRoutes
      isAdmin={isAdmin}
      visibleKeys={visibleKeys}
      landingPath={landingPath}
      canPmSingapore={canPmSingapore}
      canSalesRow={canSalesRow}
      canDubai={canDubai}
    />
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <GlobalStyles />
      <AuthProvider>
        <Gate />
      </AuthProvider>
      {/* App-wide glass confirm/alert host — replaces native window.confirm/alert. */}
      <DialogHost />
      {/* App-wide top-center toast host — success/error feedback on every write. */}
      <ToastHost />
      {/* App-wide themed tooltip host — replaces the browser's native `title`
          rectangle everywhere with a theme-matched glass capsule. */}
      <GlobalTooltip />
      {/* App-wide soft update notice: deploy → a non-blocking top-centre message
          offering a manual Refresh. Nothing auto-reloads, and it cannot be dismissed —
          it stays until the tab is refreshed (see UpdateBanner). */}
      <UpdateBanner />
      {/* [FRAME-METER 2026-08-14] Measurement tool for the 4K / high-refresh work, inert until
          Alt+Shift+F. Renders nothing at all until toggled. */}
      <FrameMeter />
    </ThemeProvider>
  );
};

export default App;
